import React, { useState, useEffect } from 'react'

const UserLogin = () => {
  const [formData, setFormData] = useState({
    inviteCode: '',
    email: '',
    password: '',
    totpCode: ''
  })
  const [tempUser, setTempUser] = useState(null)
  const [step, setStep] = useState('login') // 'login' or '2fa'
  const [notification, setNotification] = useState(null)
  const [loading, setLoading] = useState(false)
  const [retryCount, setRetryCount] = useState(0)
  const [isOnline, setIsOnline] = useState(navigator.onLine)

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true)
      if (notification?.message.includes('No internet connection')) {
        setNotification(null)
      }
    }
    const handleOffline = () => {
      setIsOnline(false)
      showNotification('No internet connection detected.', 'error')
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [notification])

  const showNotification = (message, type = 'info') => {
    setNotification({ message, type })
    setTimeout(() => setNotification(null), 5000)
  }

  const makeAuthRequest = async (url, data, timeout = 10000) => {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeout)

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
        signal: controller.signal
      })
      
      clearTimeout(timeoutId)

      if (!response.ok) {
        if (response.status === 429) {
          throw new Error('Too many attempts. Please wait a moment and try again.')
        }
        if (response.status === 500) {
          throw new Error('Server error. Please try again in a moment.')
        }
        if (response.status >= 400) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.message || `Request failed (${response.status})`)
        }
      }

      return await response.json()
    } catch (error) {
      clearTimeout(timeoutId)
      
      if (error.name === 'AbortError') {
        throw new Error('Request timeout. Please check your internet connection.')
      }
      if (!navigator.onLine) {
        throw new Error('No internet connection. Please check your network.')
      }
      throw error
    }
  }

  const handleSubmitWithRetry = async (e, isRetry = false) => {
    e?.preventDefault()
    // Removed sensitive logging
    
    setLoading(true)
    
    try {
      let data
      
      if (step === 'login') {
        // First step: login with credentials
        data = await makeAuthRequest('/.netlify/functions/auth-login-simple-jwt', {
          inviteCode: formData.inviteCode,
          email: formData.email,
          password: formData.password
        })
        
        // Login response processed
        
        if (data.requires2FA) {
          setTempUser(data.tempUser)
          setStep('2fa')
          setRetryCount(0) // Reset retry count on success
          showNotification('2FA required. Enter your 6-digit code below.', 'info')
        } else if (data.token) {
          localStorage.setItem('token', data.token)
          setRetryCount(0) // Reset retry count on success
          showNotification('Login successful! Redirecting...', 'success')
          setTimeout(() => window.location.href = '/success', 1500)
        } else {
          throw new Error(data.message || 'Invalid credentials')
        }
      } else if (step === '2fa') {
        // Second step: verify 2FA
        data = await makeAuthRequest('/.netlify/functions/auth-verify-2fa', {
          userId: tempUser.id,
          totpCode: formData.totpCode
        })
        
        // 2FA response processed
        
        if (data.success && data.token) {
          console.log('2FA Success - storing token and user data:', {
            hasToken: !!data.token,
            hasUser: !!data.user,
            userRole: data.user?.role,
            tokenLength: data.token?.length
          })
          
          localStorage.setItem('token', data.token)
          console.log('Token stored, localStorage token:', localStorage.getItem('token'))
          
          // Store user data for AuthContext
          if (data.user) {
            localStorage.setItem('user', JSON.stringify(data.user))
            console.log('User data stored:', localStorage.getItem('user'))
          }
          setRetryCount(0) // Reset retry count on success
          showNotification('Login successful! Redirecting...', 'success')
          setTimeout(() => window.location.href = '/success', 1500)
        } else {
          throw new Error(data.message || '2FA verification failed')
        }
      }
    } catch (error) {
      console.error('Login error:', error)
      
      // Check if it's a retryable error
      const isNetworkError = error.message.includes('timeout') || 
                           error.message.includes('network') || 
                           error.message.includes('Server error') ||
                           !navigator.onLine

      if (isNetworkError && retryCount < 2 && !isRetry) {
        // Show retry option for network errors
        showNotification(
          `${error.message} Click here to retry.`,
          'error'
        )
        setRetryCount(prev => prev + 1)
      } else {
        // Show final error or non-retryable error
        const errorMessage = retryCount >= 2 
          ? 'Multiple attempts failed. Please check your connection and try again later.'
          : error.message
        showNotification(errorMessage, 'error')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = (e) => handleSubmitWithRetry(e, false)

  const handleRetry = () => {
    if (retryCount < 3) {
      handleSubmitWithRetry(null, true)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-black to-red-900 relative">
      {/* Toast Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg max-w-sm transform transition-all duration-300 ${
          notification.type === 'success' ? 'bg-green-600 text-white' :
          notification.type === 'error' ? 'bg-red-600 text-white' :
          'bg-blue-600 text-white'
        }`}>
          <div className="flex items-start space-x-2">
            <div className="flex-shrink-0 mt-0.5">
              {notification.type === 'success' && (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
              {notification.type === 'error' && (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
              {notification.type === 'info' && (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
            </div>
            <div className="flex-1">
              <span className="text-sm">{notification.message.replace(' Click here to retry.', '')}</span>
              {notification.message.includes('Click here to retry') && retryCount < 3 && (
                <button 
                  onClick={handleRetry}
                  className="block mt-2 text-xs underline hover:no-underline opacity-90 hover:opacity-100 transition-opacity"
                >
                  🔄 Retry ({3 - retryCount} attempts left)
                </button>
              )}
            </div>
            <button
              onClick={() => setNotification(null)}
              className="flex-shrink-0 ml-2 text-white/70 hover:text-white transition-colors"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
      
      <div className="max-w-md w-full bg-black/80 backdrop-blur-sm border border-red-800/30 p-8 rounded-xl shadow-2xl">
        {/* VideoShareWEB Branding */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-red-500 mb-2">
            Video<span className="text-white">Share</span><span className="text-red-400">WEB</span>
          </h1>
          <p className="text-gray-300 text-sm">Private Family Video Platform</p>
        </div>
        
        <h2 className="text-2xl font-bold text-white mb-6 text-center">
          {step === 'login' ? 'Welcome Back' : '2FA Verification'}
        </h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {step === 'login' ? (
            <>
              <div className="space-y-4">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m0 0a2 2 0 012 2 2 2 0 00-2-2m-2-2v10a6 6 0 01-6 6v-10a6 6 0 016-6zM7 15a2 2 0 002-2m0 0a2 2 0 00-2-2m2 2h10a6 6 0 01-6 6v-10a6 6 0 01-6 6z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Invitation Code (optional for existing users)"
                    value={formData.inviteCode}
                    onChange={(e) => setFormData({...formData, inviteCode: e.target.value})}
                    className="w-full pl-10 pr-4 py-3 bg-gray-800/50 text-white rounded-lg border border-gray-600 focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all duration-200 placeholder-gray-400"
                  />
                </div>
                
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                    </svg>
                  </div>
                  <input
                    type="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full pl-10 pr-4 py-3 bg-gray-800/50 text-white rounded-lg border border-gray-600 focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all duration-200 placeholder-gray-400"
                    required
                  />
                </div>
                
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <input
                    type="password"
                    placeholder="Password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="w-full pl-10 pr-4 py-3 bg-gray-800/50 text-white rounded-lg border border-gray-600 focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all duration-200 placeholder-gray-400"
                    required
                  />
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="text-center mb-6">
                <div className="text-gray-300 text-lg mb-2">
                  Welcome back, <span className="text-red-400">{tempUser?.email}</span>!
                </div>
                <p className="text-gray-400 text-sm">Please enter your 2FA verification code</p>
              </div>
              
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-6 w-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="000000"
                  value={formData.totpCode}
                  onChange={(e) => setFormData({...formData, totpCode: e.target.value})}
                  className="w-full pl-12 pr-4 py-4 bg-gray-800/50 text-white rounded-lg border border-gray-600 focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all duration-200 text-center text-2xl font-mono tracking-widest placeholder-gray-500"
                  maxLength="6"
                  required
                />
              </div>
            </>
          )}
          
          <button
            type="submit"
            disabled={loading || !isOnline}
            className="w-full bg-gradient-to-r from-red-600 to-red-700 text-white py-4 px-6 rounded-lg hover:from-red-700 hover:to-red-800 transition-all duration-200 transform hover:scale-[1.02] font-semibold text-lg shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center"
          >
            {!isOnline ? (
              <>
                <svg className="w-5 h-5 mr-2 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-12.728 12.728m0 0L5.636 18.364m12.728-12.728L18.364 5.636m-12.728 12.728L5.636 18.364" />
                </svg>
                No Connection
              </>
            ) : loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {step === 'login' ? 'Signing In...' : 'Verifying...'}
              </>
            ) : (
              step === 'login' ? 'Sign In' : 'Verify Code'
            )}
          </button>
          
          {step === '2fa' && (
            <button
              type="button"
              onClick={() => setStep('login')}
              className="w-full mt-3 text-gray-400 hover:text-white transition-colors duration-200 text-sm"
            >
              ← Back to login
            </button>
          )}
        </form>
        
        {/* Connection Status */}
        <div className="mt-4 flex items-center justify-center text-xs">
          <div className={`flex items-center space-x-1 ${isOnline ? 'text-green-400' : 'text-red-400'}`}>
            <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-green-400' : 'bg-red-400'} ${isOnline ? 'animate-pulse' : ''}`}></div>
            <span>{isOnline ? 'Connected' : 'Offline'}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UserLogin